import { FormControl, FormHelperText, IconButton, Input, InputAdornment, ListItemText } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef, useState } from "react";

import { isPublicPage } from "@/utils/visualBuilder";

import { TAB_STATUSES } from "../../../../constants";
import { ArrowDownIcon, ArrowUpIcons, EyeClosed, EyeNormal, PencilGrey, Revert } from "../../../../icons";

const useStyles = makeStyles((theme) => ({
  editIcon: {
    position: "relative",
    marginLeft: "0px",
    top: "1px",
    fontSize: "1rem",
  },
  hideIcon: {
    position: "relative",
    marginLeft: "0px",
    top: "1px",
    fontSize: "1.6rem",
    stroke: "#FFF",
    fill: "none",
  },
  tagIcon: {
    top: "-3px",
    left: "-20px",
    width: ".5em",
    position: "absolute",
  },
  createNewTagIcon: {
    top: "-1.5px",
    left: "25px",
  },
  listItem: {
    color: "#fff",
    position: "relative",
    userSelect: "none",
    fontSize: "0.75rem",
  },
  createNewTagSubmitIcon: {
    fontSize: ".75em",
  },
  createNewTagError: {
    fontSize: ".5rem",
    position: "absolute",
    bottom: "-12px",
  },
  createNewTagInput: {
    marginBottom: (props) => (props.value ? "2px" : "0px"),
  },
  hideShowButton: {
    padding: "0",
  },
  actions: {
    display: "flex",
    visibility: "hidden",
  },
  row: {
    padding: "5px 0",
  },
  eyeClosed: {
    opacity: ".5",
    fontSize: "1.6rem",
  },
  underline: {
    "&:before": {
      borderBottom: "0px !important",
    },
    "&:hover:not($disabled):not($focused):not($error):before": {
      borderBottom: "0px !important",
    },
    "&:after": {
      borderBottom: "1px solid #71B6BC !important",
    },
  },
  underlineInactive: {
    "&:before": {
      borderBottom: "0px !important",
    },
    "&:hover:not($disabled):not($focused):not($error):before": {
      borderBottom: "0px !important",
    },
    "&:after": {
      borderBottom: "0px solid #71B6BC !important",
    },
  },
  input: {
    cursor: "text",
  },
  inputInactive: {
    cursor: "pointer !important",
  },
  focused: {
    "& input": {
      cursor: "text",
    },
  },
  disabled: {},
  error: {},
  active: {
    color: theme.primaryColor,
  },
  onlyrows: {
    display: "flex",
    "flex-direction": "row",
  },
  rowsgap: {
    width: "100%",
    justifyContent: "space-between",
  },
  dropdownIcon: {
    "& svg": {
      visibility: "visible !important",
    },
  },
}));

export default function TagField(props) {
  const {
    disabled = false,
    index,
    onChangeTagStatus,
    onTagNameChangeComplete,
    statusSelected,
    // searchTags = []
    tabStatus,
    tag,
    toggleDropdown,
    toggleShowChildren,
  } = props;

  const classes = useStyles(props);

  const { name, value, isEditedName, hidden, notHighlighted } = tag;
  const [createTagValue, setCreateTagValue] = useState(isEditedName || name);
  const [newTagError, setNewTagError] = useState("");
  const [hideShowIcons, setHideShowIcons] = useState(false);
  const [editStatus, setEditStatus] = useState(false);
  const [hideStatus, setHideStatus] = useState(hidden);
  const [rerender, forceRerender] = useState(false);

  useEffect(() => {
    setCreateTagValue(isEditedName || name);
  }, [name, isEditedName]);

  useEffect(() => {
    setHideStatus(hidden);
    setHideShowIcons(hidden);
  }, [hidden]);

  const onTagNameRestore = (e) => {
    onTagNameChangeComplete(tag, value);
    forceRerender(!rerender);
    e.preventDefault();
  };
  const changeName = (e) => {
    e.preventDefault();
    onTagNameChangeComplete(tag, createTagValue);
    setEditStatus(false);
  };

  let timerId = null;

  const handleChange = (e) => {
    e.persist();
    const { value } = e.target;
    setCreateTagValue(value);
    clearTimeout(timerId);
    const nameReg =
      /^[1234567890a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/u;

    timerId = setTimeout(() => {
      if (value) {
        if (value.length <= 2) {
          setNewTagError("Too short");
        } else if (value.length >= 15) {
          setNewTagError("Too long");
        } else if (!nameReg.test(value)) {
          setNewTagError("Invalid");
        } else {
          setNewTagError("");
        }
      } else {
        setNewTagError("");
      }
    }, 300);
  };

  const useFocus = () => {
    const htmlElRef = useRef(null);
    const setFocus = () => {
      return htmlElRef.current && htmlElRef.current.focus();
    };

    return [htmlElRef, setFocus];
  };
  const [inputRef, setInputFocus] = useFocus();

  const hideShow = (
    <>
      {!hidden ? (
        <IconButton
          aria-label="edit tag"
          onClick={(e) => {
            e.stopPropagation();
            setEditStatus(true);
            setInputFocus();
          }}
          size="small"
          disabled={disabled}
        >
          <PencilGrey className={classes.editIcon} htmlColor="#FFF" strokeOpacity="1" />
        </IconButton>
      ) : null}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onChangeTagStatus(tag, index);
        }}
        className={classes.hideShowButton}
        disabled={disabled}
      >
        {hideStatus ? (
          <EyeClosed className={`${classes.hideIcon} ${classes.eyeClosed}`} />
        ) : (
          <EyeNormal className={classes.hideIcon} />
        )}
      </IconButton>
    </>
  );

  const tagFieldEndAdornment =
    editStatus && tabStatus === "customize" ? (
      <InputAdornment style={{ backgroundColor: "transparent" }} position="end">
        <IconButton aria-label="revert tag" onMouseDown={onTagNameRestore} size="small" disabled={disabled}>
          <Revert className={classes.editIcon} htmlColor="#fff" />
        </IconButton>
      </InputAdornment>
    ) : (
      (hideShowIcons || hideStatus) && tabStatus === "customize" && hideShow
    );

  return editStatus ? (
    <div
      className={`${classes.row} nospace`}
      onMouseEnter={() => tabStatus === "customize" && setHideShowIcons(true)}
      onMouseLeave={() => tabStatus === "customize" && setHideShowIcons(false)}
    >
      <form onSubmit={changeName}>
        <FormControl>
          <Input
            classes={{
              input: editStatus ? classes.input : classes.inputInactive,
              focused: classes.focused,
              underline: editStatus ? classes.underline : classes.underlineInactive,
              disabled: classes.disabled,
              error: classes.error,
            }}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            margin="dense"
            value={createTagValue}
            readOnly={!!(!editStatus || tabStatus === TAB_STATUSES.SHARE || isPublicPage(tabStatus))}
            onBlur={changeName}
            onKeyPress={(e) => e.key === "Enter" && changeName(e)}
            onChange={handleChange}
            className={[classes.createNewTagInput, classes.listItem].join(" ")}
            inputProps={{
              ref: inputRef,
              style: {
                backgroundColor: "transparent",
                padding: "3px 0",
                fontSize: "16px",
                opacity: hideStatus ? 0.5 : 1,
              },
            }}
            endAdornment={tagFieldEndAdornment}
          />
          <FormHelperText className={`${classes.createNewTagError} ${classes.listItem}`}>{newTagError}</FormHelperText>
        </FormControl>
      </form>
    </div>
  ) : (
    <div
      className={`${classes.onlyrows} ${classes.rowsgap} nospace`}
      onMouseEnter={() => tabStatus === "customize" && setHideShowIcons(true)}
      onMouseLeave={() => tabStatus === "customize" && setHideShowIcons(false)}
      data-testid="tag-field-continer"
    >
      {!notHighlighted && (
        <ListItemText
          primary={createTagValue}
          style={{
            opacity: 1,
            wordBreak: "break-word",
          }}
          className={statusSelected ? classes.active : ""}
        />
      )}
      {tabStatus === "customize" && (
        <div className={classes.actions} style={{ visibility: hideShowIcons ? "visible" : "hidden" }}>
          {hideShow}
        </div>
      )}
      {!hidden && (
        <IconButton className={classes.dropdownIcon} size="small" onClick={toggleShowChildren}>
          {toggleDropdown ? (
            <ArrowUpIcons style={{ width: ".75em" }} htmlColor="#fff" />
          ) : (
            <ArrowDownIcon style={{ width: ".75em" }} htmlColor="#fff" />
          )}
        </IconButton>
      )}
    </div>
  );
}
